/** SA-1080: the words the router editor's save review and channel adaptation
 *  say. Pure strings; the model and the components import from here. */
import type { PolicyChannel } from './routerPolicyModel'

const WHAT_NEVER_HAS: Record<PolicyChannel, string> = {
  'whatsapp-group': 'a WhatsApp group message never has',
  email: 'an email never has',
}

const WHERE_NOT_OFFERED: Record<PolicyChannel, string> = {
  'whatsapp-group': 'a WhatsApp group',
  email: 'email',
}

export function ruleLeftOut(id: string, labels: string[], channel: PolicyChannel): string {
  return `Rule "${id}" left out: it names ${labels.join(', ')}, which ${WHAT_NEVER_HAS[channel]}.`
}

export function handlerNotOffered(kindId: string, handlerLabel: string, channel: PolicyChannel, noneLabel: string): string {
  return `Kind "${kindId}": "${handlerLabel}" is not offered on ${WHERE_NOT_OFFERED[channel]}, so it is set to "${noneLabel}". Pick another on its card.`
}

export function builtinHandlerNotOffered(kindId: string, handlerLabel: string, channel: PolicyChannel, noneLabel: string): string {
  return `Built-in kind "${kindId}": "${handlerLabel}" is not offered on ${WHERE_NOT_OFFERED[channel]}, so it is set to "${noneLabel}". Pick another on its card.`
}

export function leftOutHeading(channel: PolicyChannel): string {
  return `Left out for ${channel === 'whatsapp-group' ? 'a WhatsApp group' : 'email'}`
}

export const kindMissingFields = (): string => 'Give the kind a label, an id, a description and a handler.'
export const kindTaken = (id: string): string => `"${id}" is already a kind of this policy.`
export const addKindPending = (): string => 'Add the new kind or cancel it first.'
export const rawInvalidPending = (): string => 'The Raw text does not read as a policy yet. Fix it, or press Form.'
export const kindIsBuiltin = (id: string): string => `"${id}" is a built-in kind.`

// ── the save review's lines (policyChanges) ─────────────────────────────────

export const APPROVAL_WORDS = { true: 'A person approves', false: 'Runs at once', null: 'Policy default' } as const
export const approvalWords = (v: unknown): string => (v === true ? APPROVAL_WORDS.true : v === false ? APPROVAL_WORDS.false : APPROVAL_WORDS.null)
export const onOff = (v: unknown): string => (v ? 'on' : 'off')
export const BUILTIN_TITLES: Record<string, string> = { internal: 'Internal', automated: 'Automated' }
export const CONDITION_LABELS: Record<string, string> = {
  from: 'From', from_domain: 'From domain', to: 'To', sender: 'Sender', subject: 'Subject pattern',
  body: 'Body pattern', attachment: 'Attachment', auto_submitted: 'Auto-submitted',
  has_attachments: 'Has attachments', thread: 'Thread',
}
export const ruleKindWords = (confidence: number): string => (confidence >= 1 ? 'hard rule' : 'hint')

export const changeLines = {
  approvalDefault: (a: unknown, b: unknown): string => `Approval by default: ${onOff(a)} → ${onOff(b)}`,
  addsKind: (id: string, label: string, handler: string): string =>
    `Adds kind ${id} (${label}, ${handler})`,
  removesKind: (id: string, label: string): string => `Removes kind ${id} (${label})`,
  kindField: (id: string, field: string, from: string, to: string): string => `Kind ${id}: ${field} ${from} → ${to}`,
  kindChanged: (id: string, field: string): string => `Kind ${id}: ${field} changed`,
  kindsReordered: 'Kinds reordered',
  builtinField: (title: string, field: string, from: string, to: string): string => `Built-in kind ${title}: ${field} ${from} → ${to}`,
  builtinChanged: (title: string, field: string): string => `Built-in kind ${title}: ${field} changed`,
  addsRule: (id: string, confidence: number, kind: string): string =>
    `Adds rule ${id} (${confidence >= 1 ? 'hard rule, decides' : 'hint for'} ${kind})`,
  removesRule: (id: string): string => `Removes rule ${id}`,
  ruleField: (id: string, field: string, from: string, to: string): string => `Rule ${id}: ${field} ${from} → ${to}`,
  ruleType: (id: string, from: string, to: string): string => `Rule ${id}: ${from} → ${to}`,
  ruleConditions: (id: string, labels: string[]): string => `Rule ${id}: conditions changed (${labels.join(', ')})`,
  rulesReordered: 'Rules reordered',
  modelEnabled: (a: unknown, b: unknown): string => `Classify with the model: ${onOff(a)} → ${onOff(b)}`,
  modelInstruction: 'Model instruction changed',
}

// ── the save review (spec §5.2) ─────────────────────────────────────────────

export const reviewWords = {
  heading: 'What Save will change',
  save: 'Save these changes',
  back: 'Back to editing',
  nothing: 'Nothing to save: this is the policy already saved.',
  moved: 'Someone saved this routing policy after your check. Here are the changes again.',
  checking: 'Checking…',
  checkFailed: "Couldn't check the routing policy. Try again.",
} as const
